"use client";

import { parsePhoneNumberFromString } from "libphonenumber-js";
import React, { useState } from "react";
import PhoneInputWithCountrySelect, {
  getCountryCallingCode,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
} from "react-phone-number-input";
import { E164Number } from "libphonenumber-js/core";
import Image from "next/image";
import { zapierNursing } from "@/lib/utils";

// Define strict types for props
interface NursingFormProps {
  darkBackground?: boolean;
  submitButtonName?: string;
  brochureDownload?: boolean;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country_code: string;
  Status_NCLEX: string;
  Visa_Petition: string;
  Source: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  country_code?: string;
  Status_NCLEX?: string;
  Visa_Petition?: string;
}

const MILES_EMAIL_REGEX = /^\w+([\.+-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
const GENERIC_EMAIL_REGEX = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
const NAME_REGEX = /^[a-zA-Z\s\-'.]*$/;

const CountryCodeDisplay = ({ country }: { country: any }) => {
  const callingCode = country ? getCountryCallingCode(country) : "";
  return (
    <span className="inline-block min-w-[30px] text-sm font-medium">
      +{callingCode}
    </span>
  );
};

const NursingForm: React.FC<NursingFormProps> = ({
  darkBackground = false,
  submitButtonName = "Register Now",
}) => {
  const initialState: FormData = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    country_code: "",
    Status_NCLEX: "",
    Visa_Petition: "",
    Source: "Website",
  };

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [formData, setFormData] = useState<FormData>(initialState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const validateField = (value: any, type: keyof FormData): boolean => {
    switch (type) {
      case "firstName":
        return typeof value === "string" && value.trim().length >= 2;
      case "lastName":
        return typeof value === "string" && value.trim().length >= 2;
      case "Status_NCLEX":
        return typeof value === "string" && value.trim().length > 0;
      case "Visa_Petition":
        return typeof value === "string" && value.trim().length > 0;
      case "email":
        if (typeof value !== "string" || !value) return false;
        return value.endsWith("@mileseducation.com")
          ? MILES_EMAIL_REGEX.test(value)
          : GENERIC_EMAIL_REGEX.test(value);
      case "phone":
        return value
          ? isPossiblePhoneNumber(value) && isValidPhoneNumber(value)
          : false;
      default:
        return false;
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!validateField(formData.firstName, "firstName")) {
      newErrors.firstName =
        "Please enter your full name (minimum 2 characters)";
    }
    if (!validateField(formData.lastName, "lastName")) {
      newErrors.lastName = "Please enter your full name (minimum 2 characters)";
    }
    if (!validateField(formData.email, "email")) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!validateField(formData.phone, "phone")) {
      newErrors.phone = "Please enter a valid phone number";
    }
    if (!validateField(formData.Status_NCLEX, "Status_NCLEX")) {
      newErrors.Status_NCLEX = "Please select your NCLEX status";
    }
    if (!validateField(formData.Visa_Petition, "Visa_Petition")) {
      newErrors.Visa_Petition = "Please select your Visa petition status";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    if (name === "name" && !NAME_REGEX.test(value)) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value.trimStart(),
    }));

    // Clear error for the field being edited
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handlePhoneChange = (value?: string) => {
    // Simplified logic using optional chaining and nullish coalescing
    const phoneNumber = value ? parsePhoneNumberFromString(value) : null;
    const countryCode = phoneNumber ? `+${phoneNumber.countryCallingCode}` : "";

    setFormData((prev) => ({
      ...prev,
      phone: value ?? "",
      country_code: countryCode,
    }));

    if (errors.phone) {
      setErrors((prev) => ({
        ...prev,
        phone: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      // Focus on the first error field
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const field = document.querySelector(
          `[name="${firstErrorField}"]`,
        ) as HTMLElement;
        field?.focus();
      }
      return;
    }

    setIsSubmitting(true);

    let nationalPhoneNumber = formData.phone;
    try {
      const parsedPhoneNumber = parsePhoneNumberFromString(formData.phone);
      if (parsedPhoneNumber) {
        nationalPhoneNumber = parsedPhoneNumber.nationalNumber;
      }
    } catch (error) {
      console.error("Error parsing phone number:", error);
    }

    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: nationalPhoneNumber,
      country_code: formData.country_code,
      Status_NCLEX: formData.Status_NCLEX,
      Visa_Petition: formData.Visa_Petition,
      Source: formData.Source,
    };

    try {
      const { error } = await zapierNursing(payload);
      if (!error) {
        setCurrentStep(2);
      }
    } catch (error) {
      console.error("Form submission failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Derive button text from submitting state
  const buttonText = isSubmitting ? "Submitting..." : submitButtonName;

  return (
    <div className="w-auto global_form_shadow rounded-lg mx-auto">
      {currentStep === 2 ? (
        <div className="text-center py-8">
          <div className="animate-bounce mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 mx-auto text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-9-9 9 9z"
              />
            </svg>
          </div>
          <h3
            className={`md:text-2xl text-xl font-bold text-center mb-2 ${darkBackground ? "text-white" : "text-[#061A2F]"}`}
          >
            Thank you for your inquiry!
          </h3>
          <h2
            className={`md:text-xl text-lg font-normal text-center ${darkBackground ? "text-gray-200" : "text-[#666]"}`}
          >
            Our programme advisor will get back to you shortly.
          </h2>
          <div className="mt-6 p-4 rounded-lg bg-green-50 border border-green-200">
            <p className="text-green-800 text-sm">
              🎯 <strong>What's next?</strong> Check your email for confirmation
              details and expect a call from our team within 24 hours.
            </p>
          </div>
        </div>
      ) : (
        <form className="flex flex-col" onSubmit={handleSubmit} noValidate>
          <h2 className="text-xl font-semibold mb-4 text-center text-white">
            Land top nursing jobs in the USA
          </h2>
          <div className="mb-2">
            <label htmlFor="firstName" className="sr-only">
              First Name
            </label>
            <input
              id="firstName"
              type="text"
              autoComplete="off"
              name="firstName"
              placeholder="First Name*"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none ${errors.firstName ? "border-red-500" : darkBackground ? "text-[#ADADAD] bg-[#070C0F] border-[#070C0F] placeholder:text-[#ADADAD]" : "text-[#595858] bg-white border-[#DEDEDE] placeholder:text-[#595858]"}`}
            />
            {errors.firstName && (
              <p
                id="firstName-error"
                role="alert"
                className="text-red-500 text-xs mt-1 ml-1"
              >
                {errors.firstName}
              </p>
            )}
          </div>
          <div className="mb-2">
            <label htmlFor="lastName" className="sr-only">
              Last Name
            </label>
            <input
              id="lastName"
              type="text"
              autoComplete="off"
              name="lastName"
              placeholder="Last Name*"
              value={formData.lastName}
              onChange={handleInputChange}
              required
              aria-invalid={errors.lastName ? "true" : "false"}
              aria-describedby={errors.lastName ? "lastName-error" : undefined}
              className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none ${errors.lastName ? "border-red-500" : darkBackground ? "text-[#ADADAD] bg-[#070C0F] border-[#070C0F] placeholder:text-[#ADADAD]" : "text-[#595858] bg-white border-[#DEDEDE] placeholder:text-[#595858]"}`}
            />
            {errors.lastName && (
              <p
                id="lastName-error"
                role="alert"
                className="text-red-500 text-xs mt-1 ml-1"
              >
                {errors.lastName}
              </p>
            )}
          </div>
          <div className="mb-2">
            <label htmlFor="email" className="sr-only">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              autoComplete="off"
              name="email"
              placeholder="E-mail*"
              value={formData.email}
              onChange={handleInputChange}
              required
              aria-invalid={errors.email ? "true" : "false"}
              aria-describedby={errors.email ? "email-error" : undefined}
              className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none ${errors.email ? "border-red-500" : darkBackground ? "text-[#ADADAD] bg-[#070C0F] border-[#070C0F] placeholder:text-[#ADADAD]" : "text-[#595858] bg-white border-[#DEDEDE] placeholder:text-[#595858]"}`}
            />
            {errors.email && (
              <p
                id="email-error"
                role="alert"
                className="text-red-500 text-xs mt-1 ml-1"
              >
                {errors.email}
              </p>
            )}
          </div>

          <div className="mb-2">
            <label htmlFor="phone" className="sr-only">
              Phone Number
            </label>
            <PhoneInputWithCountrySelect
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={(value) =>
                handlePhoneChange(value as E164Number | undefined)
              }
              placeholder="Phone Number*"
              autoComplete="off"
              defaultCountry="PH"
              flagComponent={CountryCodeDisplay}
              className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none ${errors.phone ? "border-red-500" : darkBackground ? "text-[#ADADAD] bg-[#070C0F] border-[#070C0F] placeholder:text-[#ADADAD]" : "text-[#595858] bg-white border-[#DEDEDE] placeholder:text-[#595858]"}`}
            />
            {errors.phone && (
              <p
                id="phone-error"
                role="alert"
                className="text-red-500 text-xs mt-1 ml-1"
              >
                {errors.phone}
              </p>
            )}
          </div>
          <div className="mb-2">
            <label
              className="text-xs md:text-[13px] font-medium text-white"
              htmlFor="Status_NCLEX"
            >
              Status of NCLEX:
            </label>
            <select
              id="Status_NCLEX"
              name="Status_NCLEX"
              autoComplete="off"
              value={formData.Status_NCLEX}
              onChange={handleInputChange}
              required
              aria-invalid={errors.Status_NCLEX ? "true" : "false"}
              aria-describedby={
                errors.Status_NCLEX ? "institute-error" : undefined
              }
              className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none bg-[#070C0F] border-[#070C0F] placeholder:text-[#ADADAD] ${formData.Status_NCLEX ? "text-gray-400" : "text-gray-400 text-xs"}`}
            >
              <option value="" disabled>
                Select your NCLEX status
              </option>
              <option value="Passed">Passed</option>
              <option value="Application On-going">Application On-going</option>
              <option value="Failed">Failed</option>
              <option value="Not Taken Yet">Not Taken Yet</option>
            </select>
            {errors.Status_NCLEX && (
              <p
                id="institute-error"
                role="alert"
                className="text-red-500 text-xs mt-1 ml-1"
              >
                {errors.Status_NCLEX}
              </p>
            )}
          </div>
          <div className="mb-2">
            <label
              className="text-xs md:text-[13px] font-medium text-white"
              htmlFor="Visa_Petition"
            >
              Do you have existing U.S. Employment-Based Visa Petition?
            </label>
            <select
              id="Visa_Petition"
              name="Visa_Petition"
              autoComplete="off"
              value={formData.Visa_Petition}
              onChange={handleInputChange}
              required
              aria-invalid={errors.Visa_Petition ? "true" : "false"}
              aria-describedby={
                errors.Visa_Petition ? "institute-error" : undefined
              }
              className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none bg-[#070C0F] border-[#070C0F] placeholder:text-[#ADADAD] ${formData.Visa_Petition ? "text-gray-400" : "text-gray-400 text-xs"}`}
            >
              <option value="" disabled>
                Select your U.S. Employment-Based Visa Petition
              </option>
              <option value="Yes, my EB3 Petition is filed.">
                Yes, my EB3 Petition is filed.
              </option>
              <option value="No, but I signed a contract with U.S. Employer/Agency.">
                No, but I signed a contract with U.S. Employer/Agency.
              </option>
              <option value="None">None</option>
            </select>
            {errors.Visa_Petition && (
              <p
                id="institute-error"
                role="alert"
                className="text-red-500 text-xs mt-1 ml-1"
              >
                {errors.Visa_Petition}
              </p>
            )}
          </div>
          <div className="mb-2 ml-1 flex items-center justify-start">
            <input
              id="page"
              name="consent"
              type="checkbox"
              defaultChecked={true}
              className={`relative w-5 h-5 md:min-w-[20px] appearance-none cursor-pointer
                  rounded-full mr-2 border-2 ${darkBackground ? "border-white" : "border-black"}
                  checked:bg-[#39f] checked:border-0
                  after:content-['✓'] after:absolute after:top-1/2 after:left-1/2
                  after:-translate-x-1/2 after:-translate-y-1/2 after:text-[10px]
                  after:font-bold after:text-white after:opacity-0
                  checked:after:opacity-100 after:transition-opacity after:duration-200`}
            />

            <label
              htmlFor="page"
              className={` text-[8px] md:text-[12px] font-medium tracking-[-0.373px] cursor-pointer ${
                darkBackground ? "text-[#D5D5D5]" : "text-[#002551]"
              }`}
            >
              I agree to receive updates via
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/whatsapp_icon.webp"
                alt="Whatsapp logo"
                width={12}
                height={12}
                className="mx-[2px] inline-block"
              />
              WhatsApp, Email & SMS.
              <a
                href="/privacy"
                target="_blank"
                className={`ml-1 underline ${darkBackground ? "text-[#D5D5D5]" : "text-[#39F]"}`}
              >
                Privacy Policy
              </a>
            </label>
          </div>
          <button
            className={`font-semibold bg-[#3399FF] text-white text-xs md:text-sm py-2 capitalize rounded-[24px_24px_24px_0px] transition-all duration-200 ${
              isSubmitting
                ? "opacity-70 cursor-not-allowed"
                : "hover:bg-[#2A85FF] hover:shadow-lg"
            }`}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                {buttonText}
              </div>
            ) : (
              buttonText
            )}
          </button>
        </form>
      )}
    </div>
  );
};

export default NursingForm;
